import React, { useEffect, useState } from 'react';
import { PageCenteredLayout } from '../components/layout/PageCenteredLayout';
import {
  AddAccountDialog,
  EditAccountDialog,
  EmptyAccountsState,
  SnowflakeAccountCard,
} from '../components/snowflake-accounts';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useToken } from '../hooks/useToken';
import {
  fetchSnowflakeAccounts,
  deleteSnowflakeAccount,
  selectAccounts,
  selectLoading,
  selectError,
  clearError,
  clearDialogError,
} from '../reducers/snowflakeAccountsSlice';
import Button from '../components/controls/Button';
import Spinner from '../components/common/Spinner';
import { PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ISnowflakeAccount } from '../interfaces/ISnowflakeAccountManagement';

/**
 * Main page for managing Snowflake account connections
 *
 * Features:
 * - Display all connected Snowflake accounts in a responsive grid
 * - Add new accounts via dialog
 * - Delete existing accounts with confirmation
 * - Loading states and error handling
 * - Empty state when no accounts exist
 *
 * Layout:
 * - Page header with title, description, and "Add Account" button
 * - Grid of account cards (responsive: 1 column mobile, 2-3 columns desktop)
 * - Empty state component when no accounts
 * - Loading spinner during data fetch
 * - Error messages with retry option
 */
export default function ManageAccountsPage() {
  const dispatch = useAppDispatch();
  const token = useToken();
  const accounts = useAppSelector(selectAccounts);
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectError);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ISnowflakeAccount | null>(null);
  const [editDialogInitialStep, setEditDialogInitialStep] = useState<number>(1);
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null);

  // Fetch accounts on page load
  useEffect(() => {
    if (token) {
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to fetch accounts:', err);
      });
    }
  }, [dispatch, token]);

  // Handle opening add account dialog
  const handleOpenAddDialog = () => {
    // Clear any previous modal errors before opening
    dispatch(clearDialogError());
    setIsAddDialogOpen(true);
  };

  // Handle closing add account dialog
  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
  };

  // Handle successful account creation
  const handleAccountAdded = (accountId: number) => {
    setIsAddDialogOpen(false);
    // Refresh the account list
    if (token) {
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to refresh accounts:', err);
      });
    }
  };

  // Handle opening edit account dialog
  const handleOpenEditDialog = (account: ISnowflakeAccount) => {
    // Clear any previous modal errors before opening
    dispatch(clearDialogError());
    setEditingAccount(account);
    setEditDialogInitialStep(1);
    setIsEditDialogOpen(true);
  };

  // Handle closing edit account dialog
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingAccount(null);
    setEditDialogInitialStep(1);
  };

  // Handle successful account update
  const handleAccountUpdated = () => {
    setIsEditDialogOpen(false);
    setEditingAccount(null);
    // Refresh the account list
    if (token) {
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to refresh accounts:', err);
      });
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async (accountId: string) => {
    if (!token) return;

    setDeletingAccountId(accountId);
    try {
      await dispatch(deleteSnowflakeAccount({ token, accountId })).unwrap();
      // Refresh the account list after successful deletion
      await dispatch(fetchSnowflakeAccounts(token)).unwrap();
    } catch (err) {
      console.error('Failed to delete account:', err);
      // Error is already set in the Redux state by the rejected action
      // The error banner will display it automatically
    } finally {
      setDeletingAccountId(null);
    }
  };

  // Handle retry after error
  const handleRetry = () => {
    dispatch(clearError());
    if (token) {
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to retry fetch accounts:', err);
      });
    }
  };

  // Render loading state
  if (loading && accounts.length === 0) {
    return (
      <PageCenteredLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <Spinner size="xLarge" className="text-dataops-secondary-dark-blue mb-4" />
          <p className="text-gray-600">Loading your Snowflake accounts...</p>
        </div>
      </PageCenteredLayout>
    );
  }

  return (
    <PageCenteredLayout>
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Snowflake Account Connections</h1>
            <p className="mt-2 text-sm text-gray-600">
              Connect and manage your Snowflake accounts for deploying solutions
            </p>
          </div>
          <div className="flex-shrink-0">
            <Button intent="primary" size="medium" onClick={handleOpenAddDialog} aria-label="Add new Snowflake account">
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Account
            </Button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4 border border-red-200" role="alert" aria-live="polite">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <Button intent="danger" size="small" onClick={handleRetry}>
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && accounts.length === 0 && <EmptyAccountsState onAddAccount={handleOpenAddDialog} />}

      {/* Accounts Grid */}
      {!error && accounts.length > 0 && (
        <div className="grid grid-cols-1 gap-2">
          {accounts.map((account) => (
            <SnowflakeAccountCard
              key={account.id}
              account={account}
              onDelete={handleDeleteAccount}
              onEdit={handleOpenEditDialog}
            />
          ))}
        </div>
      )}

      {/* Loading Overlay for Deletion */}
      {deletingAccountId && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <Spinner size="large" className="text-dataops-secondary-dark-blue mb-2" />
            <p className="text-sm text-gray-600">Deleting account...</p>
          </div>
        </div>
      )}

      {/* Add Account Dialog */}
      <AddAccountDialog isOpen={isAddDialogOpen} onClose={handleCloseAddDialog} onSuccess={handleAccountAdded} />

      {/* Edit Account Dialog */}
      <EditAccountDialog
        isOpen={isEditDialogOpen}
        account={editingAccount}
        onClose={handleCloseEditDialog}
        onSuccess={handleAccountUpdated}
        initialStep={editDialogInitialStep}
      />
    </PageCenteredLayout>
  );
}
